import { useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';
import SchemaMarkup from '../../components/base/SchemaMarkup';

const supportSchema = [
  {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Support Center - Redsail Laser Malta",
    "description": "Get support for your Redsail laser machine. Access FAQs, manuals, tutorials, and contact our expert support team.",
    "url": "https://redsaillaser-malta.com/support"
  },
  {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "What materials can REDSAIL laser machines cut and engrave?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our CO2 laser machines can process a wide variety of non-metal materials including acrylic, wood, MDF, leather, fabric, paper, rubber, glass (engraving only), and many more. The specific capabilities depend on the laser power and model."
        }
      },
      {
        "@type": "Question",
        "name": "What is the difference between cutting and engraving?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Cutting goes completely through the material, while engraving removes only the surface layer to create designs, text, or images. Our machines can do both operations."
        }
      },
      {
        "@type": "Question",
        "name": "How long does delivery take?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Delivery times vary by location. For Malta and nearby regions, typical delivery is 2-4 weeks. Contact us for specific delivery estimates to your location."
        }
      },
      {
        "@type": "Question",
        "name": "What software is compatible with REDSAIL machines?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Our machines work with RDWorks (included), LightBurn, CorelDraw, AutoCAD, Adobe Illustrator, and other design software that can export to compatible formats."
        }
      },
      {
        "@type": "Question",
        "name": "Do I need special electrical requirements?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Most models work with standard 220V or 110V power supply. Industrial models may require 3-phase power. Check the specifications for your chosen model."
        }
      },
      {
        "@type": "Question",
        "name": "How often does the laser tube need replacement?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "With proper maintenance, CO2 laser tubes typically last 2,000-10,000 hours depending on usage and power level. We provide replacement tubes and installation support."
        }
      },
      {
        "@type": "Question",
        "name": "What regular maintenance is required?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Regular maintenance includes cleaning mirrors and lenses, checking water cooling system, cleaning the honeycomb table, and ensuring proper ventilation. We provide detailed maintenance guides."
        }
      },
      {
        "@type": "Question",
        "name": "Do you provide training?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes, we provide comprehensive training including machine operation, software usage, maintenance procedures, and safety guidelines. Training can be on-site or remote."
        }
      },
      {
        "@type": "Question",
        "name": "What warranty do you offer?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "All machines come with a 2-year warranty covering parts and labor. Extended warranty options are available. The laser tube has a separate 6-month warranty."
        }
      }
    ]
  },
  {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://redsaillaser-malta.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Support",
        "item": "https://redsaillaser-malta.com/support"
      }
    ]
  }
];

const faqs = [
  {
    category: 'General',
    questions: [
      {
        q: 'What materials can REDSAIL laser machines cut and engrave?',
        a: 'Our CO2 laser machines can process a wide variety of non-metal materials including acrylic, wood, MDF, leather, fabric, paper, rubber, glass (engraving only), and many more. The specific capabilities depend on the laser power and model.',
      },
      {
        q: 'What is the difference between cutting and engraving?',
        a: 'Cutting goes completely through the material, while engraving removes only the surface layer to create designs, text, or images. Our machines can do both operations.',
      },
      {
        q: 'How long does delivery take?',
        a: 'Delivery times vary by location. For Malta and nearby regions, typical delivery is 2-4 weeks. Contact us for specific delivery estimates to your location.',
      },
    ],
  },
  {
    category: 'Technical',
    questions: [
      {
        q: 'What software is compatible with REDSAIL machines?',
        a: 'Our machines work with RDWorks (included), LightBurn, CorelDraw, AutoCAD, Adobe Illustrator, and other design software that can export to compatible formats.',
      },
      {
        q: 'Do I need special electrical requirements?',
        a: 'Most models work with standard 220V or 110V power supply. Industrial models may require 3-phase power. Check the specifications for your chosen model.',
      },
      {
        q: 'How often does the laser tube need replacement?',
        a: 'With proper maintenance, CO2 laser tubes typically last 2,000-10,000 hours depending on usage and power level. We provide replacement tubes and installation support.',
      },
    ],
  },
  {
    category: 'Maintenance',
    questions: [
      {
        q: 'What regular maintenance is required?',
        a: 'Regular maintenance includes cleaning mirrors and lenses, checking water cooling system, cleaning the honeycomb table, and ensuring proper ventilation. We provide detailed maintenance guides.',
      },
      {
        q: 'Do you provide training?',
        a: 'Yes, we provide comprehensive training including machine operation, software usage, maintenance procedures, and safety guidelines. Training can be on-site or remote.',
      },
      {
        q: 'What warranty do you offer?',
        a: 'All machines come with a 2-year warranty covering parts and labor. Extended warranty options are available. The laser tube has a separate 6-month warranty.',
      },
    ],
  },
];

const resources = [
  {
    icon: 'ri-book-open-line',
    title: 'User Manuals',
    description: 'Comprehensive guides for all machine models',
  },
  {
    icon: 'ri-video-line',
    title: 'Video Tutorials',
    description: 'Step-by-step video instructions and tips',
  },
  {
    icon: 'ri-tools-line',
    title: 'Maintenance Guides',
    description: 'Keep your machine running smoothly',
  },
  {
    icon: 'ri-download-line',
    title: 'Software Downloads',
    description: 'Latest drivers and software updates',
  },
];

const SupportPage = () => {
  const [openFaq, setOpenFaq] = useState<string | null>(null);

  const toggleFaq = (id: string) => {
    setOpenFaq(openFaq === id ? null : id);
  };

  return (
    <div className="min-h-screen bg-white">
      <SchemaMarkup schema={supportSchema} />
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 bg-gradient-to-br from-gray-50 to-white">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <span className="text-emerald-500 font-semibold text-sm tracking-wider uppercase">
            Support Center
          </span>
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mt-3 mb-6">
            We're Here to Help
          </h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Get the support you need with our comprehensive resources, expert guidance, 
            and dedicated customer service team.
          </p>
        </div>
      </section>

      {/* Contact Support */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-emerald-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-phone-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Phone Support</h3>
              <p className="text-gray-600 mb-4">Talk to our experts directly</p>
              <a
                href="tel:+35699446552"
                className="text-emerald-600 font-semibold hover:text-emerald-700"
              >
                +356 9944 6552
              </a>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-mail-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">Email Support</h3>
              <p className="text-gray-600 mb-4">Send us your questions</p>
              <a
                href="mailto:grtrophy@gmail.com"
                className="text-emerald-600 font-semibold hover:text-emerald-700"
              >
                Send Email
              </a>
            </div>
            <div className="bg-emerald-50 rounded-2xl p-8 text-center hover:shadow-lg transition-all">
              <div className="w-16 h-16 bg-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-whatsapp-line text-3xl text-white"></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-2">WhatsApp</h3>
              <p className="text-gray-600 mb-4">Chat with us instantly</p>
              <a
                href="https://wa.me/35699446552"
                target="_blank"
                rel="noopener noreferrer"
                className="text-emerald-600 font-semibold hover:text-emerald-700"
              >
                Start Chat
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Resources */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Support Resources</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Access manuals, tutorials, and guides to get the most out of your laser machine.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {resources.map((resource, index) => (
              <div
                key={index}
                className="bg-white rounded-2xl p-8 text-center hover:shadow-lg transition-all cursor-pointer"
              >
                <div className="w-16 h-16 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <i className={`${resource.icon} text-3xl text-emerald-500`}></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900 mb-2">{resource.title}</h3>
                <p className="text-gray-600 text-sm">{resource.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-lg text-gray-600">
              Find answers to common questions about our laser machines.
            </p>
          </div>
          <div className="space-y-8">
            {faqs.map((category, catIndex) => (
              <div key={catIndex}>
                <h3 className="text-2xl font-bold text-gray-900 mb-4">{category.category}</h3>
                <div className="space-y-3">
                  {category.questions.map((faq, faqIndex) => {
                    const faqId = `${catIndex}-${faqIndex}`;
                    const isOpen = openFaq === faqId;
                    return (
                      <div
                        key={faqIndex}
                        className="bg-gray-50 rounded-xl overflow-hidden"
                      >
                        <button
                          onClick={() => toggleFaq(faqId)}
                          className="w-full px-6 py-4 flex items-center justify-between text-left hover:bg-gray-100 transition-colors"
                        >
                          <span className="font-semibold text-gray-900 pr-4">{faq.q}</span>
                          <i
                            className={`ri-arrow-down-s-line text-2xl text-gray-500 transition-transform flex-shrink-0 ${
                              isOpen ? 'rotate-180' : ''
                            }`}
                          ></i>
                        </button>
                        {isOpen && (
                          <div className="px-6 pb-4">
                            <p className="text-gray-600">{faq.a}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-emerald-500">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            Still Have Questions?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Our support team is ready to assist you with any questions or concerns.
          </p>
          <Link
            to="/contact"
            className="inline-block px-8 py-4 bg-white text-emerald-600 font-semibold rounded-full hover:bg-gray-100 transition-all whitespace-nowrap"
          >
            Contact Support Team
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default SupportPage;
